import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { cx, Field } from './ui.js';

export interface LocationValue {
  roomCode: string;
  rackCode: string;
  columnCode: string;
  bagCode: string;
}

export interface LocationCodes {
  rooms: string[];
  racks: Record<string, string[]>;
  columns: Record<string, string[]>;
  bags: Record<string, string[]>;
}

export function useLocationCodes() {
  return useQuery({
    queryKey: ['location-codes'],
    queryFn: () => api<LocationCodes>('/locations/codes'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Four linked inputs for Room / Almirah / Column / Bag.
 *
 * Each one is a `datalist` combobox over the codes that already exist at that
 * level, narrowed by the levels above it. A clerk picks rather than types, so
 * the ordinary path cannot produce a code that is one character off a real
 * shelf -- which is how `R-l2` once appeared beside `R-12` and quietly swallowed
 * a file.
 *
 * Typing a genuinely new code is still allowed, because during the initial
 * survey the shelves are being discovered as the work goes along. It just has
 * to be deliberate: an unrecognised code is called out as new before the form
 * will save it.
 */
export function LocationPicker({
  value,
  onChange,
  errors = {},
  disabled = false,
}: {
  value: LocationValue;
  onChange: (next: LocationValue) => void;
  errors?: Record<string, string | undefined>;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const codes = useLocationCodes();
  const listIdBase = useId();
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const rooms = codes.data?.rooms ?? [];
  const racks = codes.data?.racks[value.roomCode] ?? [];
  const columns = codes.data?.columns[`${value.roomCode}/${value.rackCode}`] ?? [];
  const bags =
    codes.data?.bags[`${value.roomCode}/${value.rackCode}/${value.columnCode}`] ?? [];

  const levels = [
    { key: 'roomCode' as const, label: t('field.room'), options: rooms, placeholder: '2' },
    { key: 'rackCode' as const, label: t('field.rack'), options: racks, placeholder: 'R-12' },
    { key: 'columnCode' as const, label: t('field.column'), options: columns, placeholder: 'C-01' },
    { key: 'bagCode' as const, label: t('field.bag'), options: bags, placeholder: 'B-0662' },
  ];

  const set = (key: keyof LocationValue) => (next: string) => {
    // Clearing a level clears the ones below it: a column from a different
    // almirah is meaningless, and leaving it behind is how mismatched
    // addresses get saved.
    const updated: LocationValue = { ...value, [key]: next };
    if (key === 'roomCode') Object.assign(updated, { rackCode: '', columnCode: '', bagCode: '' });
    if (key === 'rackCode') Object.assign(updated, { columnCode: '', bagCode: '' });
    if (key === 'columnCode') Object.assign(updated, { bagCode: '' });
    onChange(updated);
  };

  return (
    <>
      {levels.map((level, index) => {
        const current = value[level.key];
        const parentFilled = index === 0 || Boolean(value[levels[index - 1]!.key]);
        // No `options.length > 0` guard: in a brand-new room there are no
        // known almirahs yet, and that is exactly when a typo is least
        // visible. An empty list means everything typed is new, and saying so
        // is the point.
        const isNew =
          current.trim() !== '' && parentFilled && !level.options.includes(current.trim());
        const listId = `${listIdBase}-${level.key}`;

        return (
          <Field
            key={level.key}
            label={level.label}
            required
            error={errors[`location.${level.key}`]}
            hint={
              isNew && touched[level.key]
                ? t('location.newCodeWarning', { code: current.trim() })
                : undefined
            }
          >
            <input
              className={cx('field-input', isNew && touched[level.key] && 'border-amber-line')}
              value={current}
              onChange={(event) => set(level.key)(event.target.value)}
              onBlur={() => setTouched((state) => ({ ...state, [level.key]: true }))}
              placeholder={level.placeholder}
              list={listId}
              disabled={disabled || !parentFilled}
              required
              autoComplete="off"
              spellCheck={false}
            />
            <datalist id={listId}>
              {level.options.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </Field>
        );
      })}
    </>
  );
}

/**
 * A short line under the picker telling the clerk whether this exact shelf is
 * already on record, so "new almirah" is a decision rather than an accident.
 */
export function LocationStatus({ value }: { value: LocationValue }) {
  const { t } = useI18n();
  const complete =
    value.roomCode.trim() && value.rackCode.trim() && value.columnCode.trim() && value.bagCode.trim();

  const check = useQuery({
    queryKey: ['bag-exists', value.roomCode, value.rackCode, value.columnCode, value.bagCode],
    queryFn: () =>
      api<{ exists: boolean }>(
        `/locations/exists?roomCode=${encodeURIComponent(value.roomCode.trim())}` +
          `&rackCode=${encodeURIComponent(value.rackCode.trim())}` +
          `&columnCode=${encodeURIComponent(value.columnCode.trim())}` +
          `&bagCode=${encodeURIComponent(value.bagCode.trim())}`,
      ),
    enabled: Boolean(complete),
    staleTime: 30_000,
  });

  if (!complete) return null;
  if (check.isLoading || !check.data) return null;

  return check.data.exists ? (
    <p className="text-[0.8125rem] text-green-ink">{t('location.known')}</p>
  ) : (
    <p className="rounded-[5px] border border-amber-line bg-amber-bg px-3 py-2 text-[0.8125rem] text-amber-ink">
      {t('location.willBeCreated')}
    </p>
  );
}
